
import { useEffect, useState } from 'react';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersResponse = await api.get('/api/all/users');
        const assetsResponse = await api.get('/api/all/assets');
        const accountSalesResponse = await api.get('/api/all/account_sales');

        setData({
          users: usersResponse.data,
          assets: assetsResponse.data,
          account_sales: accountSalesResponse.data,
        });
      } catch (error) {
        console.error('Failed to fetch admin data', error);
      }
    };

    fetchData();
  }, []);

  const renderStatCard = (title: string, value: number, color: string) => (
    <div className={`bg-gray-800 p-6 rounded-lg border-2 border-${color}-500 shadow-lg transform transition-all duration-300 hover:scale-105`}>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
      <p className={`text-5xl font-extrabold text-${color}-400 mt-2`}>{value}</p>
    </div>
  );

  const renderCircularProgressBar = (title: string, percentage: number, color: string) => {
    const circumference = 2 * Math.PI * 50;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="bg-gray-800 p-6 rounded-lg border-2 border-gray-700 shadow-lg flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-white mb-4">{title}</h2>
        <svg className="w-32 h-32">
          <circle className="text-gray-700" strokeWidth="10" stroke="currentColor" fill="transparent" r="50" cx="60" cy="60" />
          <circle
            className={`text-${color}-500`}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r="50"
            cx="60"
            cy="60"
          />
        </svg>
        <p className={`text-3xl font-bold text-${color}-400 mt-4`}>{percentage}%</p>
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-900 font-mono text-white">
      <AdminHeader />
      <div className="flex-grow p-8">
        <h1 className="text-5xl font-bold mb-10 text-center text-yellow-400 drop-shadow-lg">Admin Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
          {renderStatCard('Total Users', data?.users?.length || 0, 'blue')}
          {renderStatCard('Total Assets', data?.assets?.length || 0, 'green')}
          {renderStatCard('Total Boostings', 0, 'purple')}
          {renderStatCard('Total Sales', data?.account_sales?.length || 0, 'red')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {renderCircularProgressBar('User Growth', data?.users?.length ? Math.min(100, data.users.length) : 0, 'teal')}
          {renderCircularProgressBar('Asset Count', data?.assets?.length ? Math.min(100, data.assets.length) : 0, 'orange')}
          {renderCircularProgressBar('Account Sales', data?.account_sales?.length ? Math.min(100, data.account_sales.length) : 0, 'pink')}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-white mb-4">Recent Users</h2>
            <ul className="space-y-2">
              {data?.users?.slice(0, 5).map((user: any) => (
                <li key={user.id} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                  <span>{user.name || user.email}</span>
                  <span className="text-sm text-gray-400">{new Date(user.createdAt).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-white mb-4">Recent Account Sales</h2>
            <ul className="space-y-2">
              {data?.account_sales?.slice(0, 5).map((sale: any) => (
                <li key={sale.id} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                  <span>{sale.title}</span>
                  <span className="text-sm text-gray-400">${sale.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10 text-center">
          <Link to="/manageuser" className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-105">
            Manage Users
          </Link>
        </div>
      </div>
      <AdminFooter />
    </div>
  );
};

export default AdminDashboard;

